import { service } from '@ember/service';
import Component from '@glimmer/component';
import { dropTask } from 'ember-concurrency';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type { AtlasDisplay } from 'garaje/utils/atlas';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import urlBuilder from 'garaje/utils/url-builder';
import { defer } from 'rsvp';

interface AtlasTableComponentProps {
  displays: AtlasDisplay[];
  removeDisplay: (id: string) => void;
}

export default class AtlasTableComponent extends Component<AtlasTableComponentProps> {
  @service declare flashMessages: FlashMessagesService;
  @service declare ajax: AjaxFetchService;

  // Sends a message to the display to trigger a reload
  @dropTask
  refreshDisplayTask = {
    *perform(
      this: {
        context: AtlasTableComponent;
        abort?: () => void;
        continue?: (id: string) => Promise<void>;
      },
      id: string,
    ): Generator<Promise<boolean>, void, void> {
      const deferred = defer<boolean>();

      this.abort = () => {
        deferred.resolve(false);
      };

      this.continue = async () => {
        try {
          const url = urlBuilder.mapi.studio.refreshUrl(id);

          await this.context.ajax.request(url, {
            type: 'POST',
          });

          this.context.flashMessages.showAndHideFlash('success', 'Refreshed display!');
        } catch (e) {
          const errorText = parseErrorForDisplay(e);
          this.context.flashMessages.showAndHideFlash('error', errorText);
        }

        deferred.resolve(true);
      };

      return yield deferred.promise;
    },
  };

  // Immediately logs out the display
  @dropTask
  removeDisplayTask = {
    *perform(
      this: {
        context: AtlasTableComponent;
        abort?: () => void;
        continue?: (id: string) => Promise<void>;
      },
      id: string,
    ): Generator<Promise<boolean>, void, void> {
      const deferred = defer<boolean>();

      this.abort = () => {
        deferred.resolve(false);
      };

      this.continue = async () => {
        try {
          const url = urlBuilder.mapi.devices.deviceUrl(id);

          await this.context.ajax.request(url, {
            type: 'DELETE',
          });

          this.context.args.removeDisplay(id);
          this.context.flashMessages.showAndHideFlash('success', 'Removed display!');
        } catch (e) {
          const errorText = parseErrorForDisplay(e);
          this.context.flashMessages.showAndHideFlash('error', errorText);
        }

        deferred.resolve(true);
      };

      return yield deferred.promise;
    },
  };
}
